/**
 * @description Tipos e constantes para dados veterinários
 * @module VetTypes
 * @since Outubro 2025
 */

/**
 * Interface para definir uma espécie animal no formato FHIR CodeableConcept
 */
export interface VetAnimalSpecies {
  code: string;
  display: string;
  system: string;
}

/**
 * Sistema de código padrão para espécies de animais
 */
export const VET_SPECIES_SYSTEM =
  "http://fluxmed.com.br/fhir/CodeSystem/especies-animais";

/**
 * Interface para definir o status de castração do animal (HL7 animal-genderstatus)
 */
export interface VetGenderStatus {
  code: string;
  display: string;
  system: string;
}

/**
 * Sistema de código padrão para status de castração (HL7)
 */
export const VET_GENDER_STATUS_SYSTEM =
  "http://hl7.org/fhir/animal-genderstatus";

/**
 * Interface para definir uma cor de animal
 */
export interface VetAnimalColor {
  code: string;
  display: string;
  system: string;
}

/**
 * Sistema de código padrão para cores de animais
 */
export const VET_COLOR_SYSTEM =
  "http://fluxmed.com.br/fhir/CodeSystem/cor-animal";

/**
 * Lista de cores de animais disponíveis no sistema
 * Cada cor possui um código em inglês, nome de exibição em português e sistema de código
 *
 * @example
 * // Exemplo de uso
 * const color = VET_ANIMAL_COLORS.find(c => c.code === "black");
 * console.log(color?.display); // "Preto"
 */
export const VET_ANIMAL_COLORS: VetAnimalColor[] = [
  {
    code: "black",
    display: "Preto",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "white",
    display: "Branco",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "brown",
    display: "Marrom",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "gray",
    display: "Cinza",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "caramel",
    display: "Caramelo",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "brindle",
    display: "Tigrado",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "tricolor",
    display: "Tricolor",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "spotted",
    display: "Malhado",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "golden",
    display: "Dourado",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "cream",
    display: "Creme",
    system: VET_COLOR_SYSTEM,
  },
  {
    code: "other",
    display: "Outra",
    system: VET_COLOR_SYSTEM,
  },
];

/**
 * Lista de status de castração disponíveis (HL7 animal-genderstatus)
 * @see http://hl7.org/fhir/animal-genderstatus
 */
export const VET_GENDER_STATUS: VetGenderStatus[] = [
  {
    code: "neutered",
    display: "Sim (Castrado)",
    system: VET_GENDER_STATUS_SYSTEM,
  },
  {
    code: "intact",
    display: "Não (Intacto)",
    system: VET_GENDER_STATUS_SYSTEM,
  },
  {
    code: "unknown",
    display: "Desconhecido",
    system: VET_GENDER_STATUS_SYSTEM,
  },
];

/**
 * Lista de espécies de animais disponíveis no sistema
 * Cada espécie possui um código, nome de exibição e sistema de código
 *
 * @example
 * // Exemplo de uso
 * const species = VET_ANIMAL_SPECIES.find(s => s.code === "dog");
 * console.log(species?.display); // "Cão (Cachorro)"
 */
export const VET_ANIMAL_SPECIES: VetAnimalSpecies[] = [
  {
    code: "dog",
    display: "Cão (Cachorro)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "cat",
    display: "Gato",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "horse",
    display: "Equino (Cavalo)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "cattle",
    display: "Bovino (Vaca/Boi)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "pig",
    display: "Suíno (Porco)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "sheep",
    display: "Ovino (Ovelha)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "goat",
    display: "Caprino (Cabra)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "rabbit",
    display: "Coelho",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "bird",
    display: "Ave (Pássaro)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "parrot",
    display: "Papagaio",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "chicken",
    display: "Galináceo (Galinha)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "hamster",
    display: "Hamster",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "guinea-pig",
    display: "Porquinho-da-índia",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "ferret",
    display: "Furão (Ferret)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "fish",
    display: "Peixe",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "reptile",
    display: "Réptil (Geral)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "lizard",
    display: "Lagarto",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "snake",
    display: "Serpente (Cobra)",
    system: VET_SPECIES_SYSTEM,
  },
  {
    code: "other",
    display: "Outro",
    system: VET_SPECIES_SYSTEM,
  },
];

/**
 * Type helper para obter os códigos de espécies
 */
export type VetSpeciesCode = (typeof VET_ANIMAL_SPECIES)[number]["code"];

/**
 * Função helper para buscar uma espécie por código
 * @param code - Código da espécie
 * @returns Dados da espécie ou undefined se não encontrado
 */
export function getSpeciesByCode(code: string): VetAnimalSpecies | undefined {
  return VET_ANIMAL_SPECIES.find((species) => species.code === code);
}

/**
 * Função helper para buscar uma espécie por nome de exibição
 * @param display - Nome de exibição da espécie
 * @returns Dados da espécie ou undefined se não encontrado
 */
export function getSpeciesByDisplay(
  display: string
): VetAnimalSpecies | undefined {
  return VET_ANIMAL_SPECIES.find((species) => species.display === display);
}

/**
 * Função helper para buscar um status de castração por código
 * @param code - Código do status (neutered, intact, unknown)
 * @returns Dados do status ou undefined se não encontrado
 */
export function getGenderStatusByCode(
  code: string
): VetGenderStatus | undefined {
  return VET_GENDER_STATUS.find((status) => status.code === code);
}

/**
 * Função helper para buscar um status de castração por nome de exibição
 * @param display - Nome de exibição do status
 * @returns Dados do status ou undefined se não encontrado
 */
export function getGenderStatusByDisplay(
  display: string
): VetGenderStatus | undefined {
  return VET_GENDER_STATUS.find((status) => status.display === display);
}

/**
 * Função helper para buscar uma cor por código
 * @param code - Código da cor
 * @returns Dados da cor ou undefined se não encontrado
 */
export function getColorByCode(code: string): VetAnimalColor | undefined {
  return VET_ANIMAL_COLORS.find((color) => color.code === code);
}

/**
 * Função helper para buscar uma cor por nome de exibição
 * @param display - Nome de exibição da cor
 * @returns Dados da cor ou undefined se não encontrado
 */
export function getColorByDisplay(display: string): VetAnimalColor | undefined {
  return VET_ANIMAL_COLORS.find((color) => color.display === display);
}
